import { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { organizationTeamPath } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatDate } from '../../../shared/utils/dateFormat';
import { OrganizationV2 } from '../../../types/organization';
import { formatOwnerLabel } from '../../../utils/formatOwnerLabel';
import { MobileStat } from '../components/MobileCardParts';
import { organizationPremiumPath } from './OrganizationsTable';

interface MobileOrganizationCardProps {
  organization: OrganizationV2;
  onEdit: () => void;
}

export const MobileOrganizationCard = memo(function MobileOrganizationCard({
  organization,
  onEdit,
}: MobileOrganizationCardProps) {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const ownerLabel = formatOwnerLabel(organization.owner);

  return (
    <div className="mobile-station-card">
      <div className="mobile-card-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <span className="mobile-card-id-label">
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: '0.875rem',
                marginRight: '0.25rem',
                verticalAlign: 'middle',
              }}
              aria-hidden="true"
            >
              corporate_fare
            </span>
            {t('dashboard.organizations.columnName')}
          </span>
          <h3
            className="mobile-card-id-value"
            style={{
              fontSize: '0.9375rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {organization.name}
          </h3>
        </div>
        <span
          className={`mobile-status-badge ${
            organization.hasMonobankToken
              ? 'mobile-status-badge-active'
              : 'mobile-status-badge-inactive'
          }`}
        >
          {organization.hasMonobankToken
            ? t('dashboard.organizations.connected')
            : t('dashboard.organizations.notConnected')}
        </span>
      </div>

      <div className="mobile-card-stats">
        <MobileStat
          icon="person"
          label={t('dashboard.organizations.columnOwner')}
          value={ownerLabel}
        />
        <MobileStat
          icon="ev_station"
          label={t('dashboard.organizations.columnStations')}
          value={organization.stationCount}
        />
        <MobileStat
          icon="calendar_month"
          label={t('dashboard.organizations.columnCreated')}
          value={formatDate(organization.createdAt ?? '')}
        />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '0.75rem', gap: '0.5rem' }}>
        {has('organizations.manage') && (
          <Link className="secondary-action-btn" to={organizationTeamPath(organization.id)}>
            <span className="material-symbols-outlined" aria-hidden="true">groups</span>
            {t('dashboard.organizations.teamAction')}
          </Link>
        )}
        <Link className="secondary-action-btn" to={organizationPremiumPath(organization.id)}>
          <span className="material-symbols-outlined" aria-hidden="true">workspace_premium</span>
          {t('dashboard.premium.orgTitle')}
        </Link>
        <button type="button" className="secondary-action-btn" onClick={onEdit}>
          <span className="material-symbols-outlined" aria-hidden="true">edit</span>
          {t('dashboard.organizations.edit')}
        </button>
      </div>
    </div>
  );
});

export default MobileOrganizationCard;
